import {
  AddOutlined,
  ArrowBackOutlined,
  ArchiveOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  ExpandMoreOutlined,
  Inventory2Outlined,
  SearchOutlined,
  ScienceOutlined
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ProductItem,
  ProductPackaging,
  MaterialStockPolicy,
  archiveProduct,
  archiveProductPackaging,
  checkCompatibility,
  changeProductStatus,
  createPackaging,
  createMaterialStockPolicy,
  createProduct,
  isAuthenticationError,
  loadPackaging,
  loadPackagingDefinitions,
  loadMaterialStockPolicies,
  loadMaterialStockPolicyEvaluations,
  loadReplenishmentOutputProfiles,
  loadSuppliers,
  loadProduct,
  loadProductAudit,
  loadProducts,
  loadWarehouseStructure,
  updateProduct,
  updateProductRotationPolicy
  ,updateMaterialStockPolicy
} from "./api";
import { login } from "./auth";
import { matchesListSyntax } from "./listSearch";
import { PageHeader } from "./PageHeader";
import { MeasurementValue } from "./MeasurementValue";
import {
  clampNonNegativeMeasurementInput, measurementInputStep, measurementInputValue,
  measurementUnit, toMetric
} from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";

const organizationId = "10000000-0000-0000-0000-000000000001";

export function ProductMasterData({
  onBack,
  permissions,
  initialSearch = "",
  initialTargetId = null
}: {
  onBack: () => void;
  permissions: string[];
  initialSearch?: string;
  initialTargetId?: string | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<ProductItem | null>(null);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [baseUom, setBaseUom] = useState("EA");
  const [abcClass, setAbcClass] = useState<"A" | "B" | "C" | "">("");
  const [supplierIds, setSupplierIds] = useState<string[]>([]);
  const [primarySupplierId, setPrimarySupplierId] = useState("");
  const [lotTracking, setLotTracking] = useState(false);
  const [serialTracking, setSerialTracking] = useState(false);
  const [expiryTracking, setExpiryTracking] = useState(false);
  const [shelfLife, setShelfLife] = useState("");
  const [minimumShelfLife, setMinimumShelfLife] = useState("0");
  const [rotationPolicy, setRotationPolicy] =
    useState<"FIFO" | "FEFO" | "LIFO" | "NONE">("FIFO");
  const [lotMixingAllowed, setLotMixingAllowed] = useState(true);
  const [partialHuAllowed, setPartialHuAllowed] = useState(true);
  const [esd, setEsd] = useState(false);
  const [hazmat, setHazmat] = useState(false);
  const [listTab, setListTab] = useState<"active" | "archived">("active");
  const [syntaxSearch, setSyntaxSearch] = useState(initialSearch);

  const products = useQuery({ queryKey: ["products"], queryFn: loadProducts });
  const suppliers = useQuery({ queryKey: ["suppliers"], queryFn: loadSuppliers });
  useEffect(() => {
    setSyntaxSearch(initialSearch);
    if (!initialTargetId) return;
    const product = products.data?.find((item) => item.id === initialTargetId);
    if (product) {
      setListTab(product.status === "ARCHIVED" ? "archived" : "active");
      setSelected(product);
    }
  }, [initialSearch, initialTargetId, products.data]);
  const filteredProducts = useMemo(() => (products.data ?? []).filter((item) =>
    (item.status === "ARCHIVED") === (listTab === "archived")
    && (!syntaxSearch.trim() || matchesListSyntax({
      id: `${item.id} ${item.code}`,
      libelle: item.displayName,
      uom: item.baseUom,
      statut: item.status,
      lot: item.lotTracking,
      serie: item.serialTracking,
      peremption: item.expiryTracking,
      rotation: item.rotationPolicy,
      esd: item.esdSensitive,
      hazmat: item.hazardousMaterial
    }, syntaxSearch))
  ), [listTab, products.data, syntaxSearch]);
  const create = useMutation({
    mutationFn: createProduct,
    onSuccess: async (item) => {
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      setCreateOpen(false);
      setSelected(item);
    }
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    create.mutate({
      organizationId, clientId: null, supplierIds, primarySupplierId,
      code, displayName: name, baseUom,
      lotTracking, serialTracking, expiryTracking,
      shelfLifeDays: expiryTracking ? Number(shelfLife) : null,
      minimumRemainingShelfLifeDays: expiryTracking
        ? Number(minimumShelfLife) : null,
      rotationPolicy, lotMixingAllowed, partialHuAllowed,
      esdSensitive: esd, hazardousMaterial: hazmat,
      minStorageTemperatureC: null, maxStorageTemperatureC: null,
      abcClass: abcClass || null
    });
  }

  return (
    <Stack spacing={1.5} sx={{
      height: { md: "100%" }, minHeight: 0
    }}>
      <PageHeader
        title={t("masterData.products.title")}
        compact
        onBack={onBack}
        actions={
          <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setCreateOpen(true)}>
            {t("masterData.products.new")}
          </Button>
        }
      />

      {products.isError && (
        <Alert
          severity="error"
          action={isAuthenticationError(products.error)
            ? <Button color="inherit" size="small" onClick={() => void login()}>
                {t("masterData.common.reconnect")}
              </Button>
            : <Button color="inherit" size="small" onClick={() => void products.refetch()}>
                {t("masterData.common.retry")}
              </Button>}
        >
          {isAuthenticationError(products.error)
            ? t("masterData.common.sessionExpired")
            : t("masterData.products.loadError")}
        </Alert>
      )}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <TextField fullWidth label={t("masterData.common.search")}
            placeholder={'id:MAT0001 libelle:"material 1" serie:true'}
            value={syntaxSearch}
            onChange={(event) => setSyntaxSearch(event.target.value)}
            InputProps={{ startAdornment:
              <SearchOutlined color="action" sx={{ mr: 1 }} /> }} />
        </CardContent>
      </Card>
      <Tabs value={listTab}
          onChange={(_, value: "active" | "archived") => setListTab(value)}>
          <Tab value="active" label={`${t("masterData.common.active")} (${products.data?.filter(
            (item) => item.status !== "ARCHIVED").length ?? 0})`} />
          <Tab value="archived" label={`${t("masterData.common.archived")} (${products.data?.filter(
            (item) => item.status === "ARCHIVED").length ?? 0})`} />
      </Tabs>
      <Box sx={{
        minHeight: 0, flex: { md: 1 }, overflowY: { md: "auto" },
        pr: { md: .5 }
      }}>
      <Stack spacing={1.5}>
        {(products.data?.length ?? 0) > 0 && filteredProducts.length === 0 && (
          <Alert severity="info">
            {t("masterData.common.noSearchResult")}
          </Alert>
        )}
        {filteredProducts.map((item) => (
          <Card key={item.id} variant="outlined" onClick={() => setSelected(item)}
            sx={{ cursor: "pointer" }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Inventory2Outlined color="secondary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={800}>{item.code} · {item.displayName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("masterData.products.baseUnitValue", { unit: item.baseUom, rotation: item.rotationPolicy })}
                  </Typography>
                </Box>
                {item.esdSensitive && <Chip size="small" label="ESD" />}
                {item.hazardousMaterial && <Chip size="small" color="warning" label="HAZMAT" />}
                <Chip size="small" color={item.status === "ACTIVE" ? "success" : "default"}
                  label={t(`masterData.common.statuses.${item.status}`, { defaultValue: item.status })} />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submit}>
          <DialogTitle>{t("masterData.products.create")}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField label={t("masterData.products.code")} required value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())} />
              <TextField label={t("masterData.common.name")} required value={name}
                onChange={(e) => setName(e.target.value)} />
              <TextField label={t("masterData.products.baseUnit")} required value={baseUom}
                onChange={(e) => setBaseUom(e.target.value.toUpperCase())} />
              <TextField select label={t("masterData.products.abcClass")} value={abcClass}
                onChange={(event) => setAbcClass(event.target.value as "A" | "B" | "C" | "")}>
                <MenuItem value="">{t("masterData.products.abcUnclassified")}</MenuItem>
                {(["A", "B", "C"] as const).map((value) => <MenuItem key={value} value={value}>{t("masterData.products.abcClassValue", { value })}</MenuItem>)}
              </TextField>
              <FormControl fullWidth required><InputLabel>{t("masterData.products.suppliers")}</InputLabel>
                <Select multiple value={supplierIds} label={t("masterData.products.suppliers")}
                  onChange={(event) => {
                    const values = typeof event.target.value === "string"
                      ? event.target.value.split(",") : event.target.value;
                    setSupplierIds(values);
                    if (!values.includes(primarySupplierId)) setPrimarySupplierId(values[0] ?? "");
                  }}>
                  {(suppliers.data ?? []).filter((entry) => entry.status === "ACTIVE").map((entry) =>
                    <MenuItem key={entry.id} value={entry.id}>{entry.code} · {entry.displayName}</MenuItem>)}
                </Select></FormControl>
              <TextField select required label={t("masterData.products.primarySupplier")}
                value={primarySupplierId} disabled={!supplierIds.length}
                onChange={(event) => setPrimarySupplierId(event.target.value)}>
                {(suppliers.data ?? []).filter((entry) => supplierIds.includes(entry.id)).map((entry) =>
                  <MenuItem key={entry.id} value={entry.id}>{entry.code} · {entry.displayName}</MenuItem>)}
              </TextField>
              <FormControlLabel control={<Checkbox checked={lotTracking}
                onChange={(e) => {
                  setLotTracking(e.target.checked);
                  if (!e.target.checked && rotationPolicy === "FEFO") setRotationPolicy("FIFO");
                }} />} label={t("masterData.products.lotTracking")} />
              <FormControlLabel control={<Checkbox checked={serialTracking}
                onChange={(e) => setSerialTracking(e.target.checked)} />} label={t("masterData.products.serialTracking")} />
              <FormControlLabel control={<Checkbox checked={expiryTracking}
                onChange={(e) => {
                  setExpiryTracking(e.target.checked);
                  if (e.target.checked) setLotTracking(true);
                  setRotationPolicy(e.target.checked ? "FEFO" : "FIFO");
                }} />} label={t("masterData.products.expiryTracking")} />
              {expiryTracking && <TextField type="number" label={t("masterData.products.shelfLife")} required
                value={shelfLife} onChange={(e) => setShelfLife(e.target.value)} />}
              {expiryTracking && <TextField type="number"
                label={t("masterData.products.minimumShelfLife")}
                value={minimumShelfLife}
                onChange={(e) => setMinimumShelfLife(e.target.value)} />}
              <FormControl fullWidth>
                <InputLabel>{t("masterData.products.rotation")}</InputLabel>
                <Select value={rotationPolicy} label={t("masterData.products.rotation")}
                  onChange={(event) => {
                    const policy = event.target.value as "FIFO" | "FEFO" | "LIFO" | "NONE";
                    setRotationPolicy(policy);
                    if (policy === "FEFO") setLotTracking(true);
                  }}>
                  <MenuItem value="FIFO">{t("masterData.products.fifo")}</MenuItem>
                  {expiryTracking && <MenuItem value="FEFO">{t("masterData.products.fefoDescription")}</MenuItem>}
                  <MenuItem value="LIFO">{t("masterData.products.lifo")}</MenuItem>
                  <MenuItem value="NONE">{t("masterData.products.noRotation")}</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel control={<Checkbox checked={lotMixingAllowed}
                onChange={(e) => setLotMixingAllowed(e.target.checked)} />}
                label={t("masterData.products.lotMixing")} />
              <FormControlLabel control={<Checkbox checked={partialHuAllowed}
                onChange={(e) => setPartialHuAllowed(e.target.checked)} />}
                label={t("masterData.products.partialPicking")} />
              <FormControlLabel control={<Checkbox checked={esd}
                onChange={(e) => setEsd(e.target.checked)} />} label={t("masterData.products.esd")} />
              <FormControlLabel control={<Checkbox checked={hazmat}
                onChange={(e) => setHazmat(e.target.checked)} />} label={t("masterData.products.hazmat")} />
              {create.isError && <Alert severity="error">{t("masterData.common.createError")}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>{t("masterData.common.cancel")}</Button>
            <Button type="submit" variant="contained" disabled={!supplierIds.length || !primarySupplierId}>{t("masterData.common.create")}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <ProductDetail
        item={selected}
        onItemChange={setSelected}
        onClose={() => setSelected(null)}
        canManageRotation={permissions.includes("product:manage_rotation_policy")}
        canManage={permissions.includes("product:manage")}
        canConfigureReplenishment={permissions.includes("replenishment:configure")}
      />
    </Stack>
  );
}

function ProductDetail({
  item,
  onItemChange,
  onClose,
  canManageRotation
  ,canManage,
  canConfigureReplenishment
}: {
  item: ProductItem | null;
  onItemChange: (item: ProductItem) => void;
  onClose: () => void;
  canManageRotation: boolean;
  canManage: boolean;
  canConfigureReplenishment: boolean;
}) {
  const { t } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const massUnit = measurementUnit("MASS", measurements);
  const massStep = measurementInputStep("MASS", measurements);
  const queryClient = useQueryClient();
  const [packagingDefinitionId, setPackagingDefinitionId] = useState("");
  const [uom, setUom] = useState("EA");
  const [baseQty, setBaseQty] = useState("1");
  const [netWeight, setNetWeight] = useState("9");
  const [grossWeight, setGrossWeight] = useState("10");
  const [packagingLevel, setPackagingLevel] =
    useState<"EACH" | "INNER" | "CASE" | "PALLET">("EACH");
  const [basePackaging, setBasePackaging] = useState(true);
  const [packagingValidationVisible, setPackagingValidationVisible] = useState(false);
  const [selectedPackaging, setSelectedPackaging] = useState("");
  const [selectedBin, setSelectedBin] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedAbcClass, setEditedAbcClass] = useState<"A" | "B" | "C" | "">("");
  const [editedSupplierIds, setEditedSupplierIds] = useState<string[]>([]);
  const [editedPrimarySupplierId, setEditedPrimarySupplierId] = useState("");
  const [rotationOpen, setRotationOpen] = useState(false);
  const [editedRotationPolicy, setEditedRotationPolicy] =
    useState<"FIFO" | "FEFO" | "LIFO" | "NONE">("FIFO");
  const [editedMinimumShelfLife, setEditedMinimumShelfLife] = useState("0");
  const [editedLotMixing, setEditedLotMixing] = useState(true);
  const [editedPartialHu, setEditedPartialHu] = useState(true);
  const [rotationReason, setRotationReason] = useState("");
  const [packagingToArchive, setPackagingToArchive] =
    useState<ProductPackaging | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [editedPolicy, setEditedPolicy] = useState<MaterialStockPolicy | null>(null);
  const [policyWarehouseId, setPolicyWarehouseId] = useState("");
  const [policySafety, setPolicySafety] = useState("0");
  const [policyMinimum, setPolicyMinimum] = useState("0");
  const [policyMaximum, setPolicyMaximum] = useState("1");
  const [policyMultiple, setPolicyMultiple] = useState("1");
  const [policyMinimumOrder, setPolicyMinimumOrder] = useState("");
  const [policyLeadTime, setPolicyLeadTime] = useState("0");
  const [policyExecutionMode, setPolicyExecutionMode] =
    useState<"DIRECT_SUPPLIER_ORDER" | "PURCHASE_REQUEST">("DIRECT_SUPPLIER_ORDER");
  const [policyOutputProfileId, setPolicyOutputProfileId] = useState("");
  const [policyStatus, setPolicyStatus] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");

  useEffect(() => {
    setNetWeight(measurementInputValue(9, "MASS", measurements));
    setGrossWeight(measurementInputValue(10, "MASS", measurements));
  }, [measurements]);

  const packaging = useQuery({
    queryKey: ["packaging", item?.id],
    queryFn: () => loadPackaging(item!.id),
    enabled: Boolean(item)
  });
  const packagingDefinitions = useQuery({
    queryKey: ["packaging-definitions"],
    queryFn: loadPackagingDefinitions,
    enabled: Boolean(item)
  });
  const audit = useQuery({
    queryKey: ["product-audit", item?.id],
    queryFn: () => loadProductAudit(item!.id),
    enabled: Boolean(item)
  });
  const structure = useQuery({
    queryKey: ["warehouse-structure"], queryFn: loadWarehouseStructure,
    enabled: Boolean(item)
  });
  const stockPolicies = useQuery({
    queryKey: ["material-stock-policies", item?.id],
    queryFn: () => loadMaterialStockPolicies(item!.id),
    enabled: Boolean(item)
  });
  const suppliers = useQuery({
    queryKey: ["suppliers"], queryFn: loadSuppliers, enabled: Boolean(item)
  });
  const replenishmentOutputProfiles = useQuery({
    queryKey: ["replenishment-output-profiles"],
    queryFn: loadReplenishmentOutputProfiles,
    enabled: Boolean(item) && canConfigureReplenishment
  });
  const stockPolicyEvaluations = useQuery({
    queryKey: ["material-stock-policy-evaluations", item?.id],
    queryFn: () => loadMaterialStockPolicyEvaluations(item!.id),
    enabled: Boolean(item)
  });
  const saveStockPolicy = useMutation({
    mutationFn: () => {
      const request = {
        warehouseId: policyWarehouseId,
        safetyStockQuantity: Number(policySafety),
        minimumQuantity: Number(policyMinimum),
        maximumQuantity: Number(policyMaximum),
        replenishmentMultiple: Number(policyMultiple),
        minimumReplenishmentQuantity: policyMinimumOrder === ""
          ? null : Number(policyMinimumOrder),
        leadTimeDays: Number(policyLeadTime),
        executionMode: policyExecutionMode,
        outputProfileId: policyOutputProfileId || null,
        ...(editedPolicy ? { status: policyStatus, version: editedPolicy.version } : {})
      };
      return editedPolicy
        ? updateMaterialStockPolicy(item!.id, editedPolicy.id, request)
        : createMaterialStockPolicy(item!.id, request);
    },
    onSuccess: async () => {
      setPolicyOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["material-stock-policies", item?.id] }),
        queryClient.invalidateQueries({ queryKey: ["material-stock-policy-evaluations", item?.id] }),
        queryClient.invalidateQueries({ queryKey: ["product-audit", item?.id] })
      ]);
    }
  });
  const addPackaging = useMutation({
    mutationFn: () => createPackaging(item!.id, {
      packagingDefinitionId, uom, baseQuantity: Number(baseQty),
      netWeightKg: toMetric(Number(netWeight), "MASS", measurements),
      grossWeightKg: toMetric(Number(grossWeight), "MASS", measurements),
      packagingLevel, basePackaging
    }),
    onSuccess: async (createdPackaging) => {
      const [, , , updatedItem] = await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["packaging", item?.id] }),
        queryClient.invalidateQueries({ queryKey: ["product-audit", item?.id] }),
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        loadProduct(item!.id)
      ]);
      onItemChange(updatedItem);
      setPackagingDefinitionId("");
      setPackagingValidationVisible(false);
      setSelectedPackaging(createdPackaging.id);
    }
  });
  const editItem = useMutation({
    mutationFn: () => updateProduct(
      item!.id, editedName.trim(), editedAbcClass || null,
      editedSupplierIds, editedPrimarySupplierId, item!.version
    ),
    onSuccess: async (updatedItem) => {
      onItemChange(updatedItem);
      setEditOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["product-audit", item?.id] })
      ]);
    }
  });
  const editRotation = useMutation({
    mutationFn: () => updateProductRotationPolicy(item!.id, {
      rotationPolicy: editedRotationPolicy,
      minimumRemainingShelfLifeDays: item!.expiryTracking
        ? Number(editedMinimumShelfLife) : null,
      lotMixingAllowed: editedLotMixing,
      partialHuAllowed: editedPartialHu,
      reason: rotationReason.trim(),
      version: item!.version
    }),
    onSuccess: async (updatedItem) => {
      onItemChange(updatedItem);
      setRotationOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["product-audit", item?.id] })
      ]);
    }
  });
  const archiveItem = useMutation({
    mutationFn: () => archiveProduct(item!.id, item!.version),
    onSuccess: async () => {
      setArchiveOpen(false);
      onClose();
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    }
  });
  const archivePackaging = useMutation({
    mutationFn: () => archiveProductPackaging(
      item!.id, packagingToArchive!.id, packagingToArchive!.version
    ),
    onSuccess: async () => {
      setPackagingToArchive(null);
      const updatedItem = await loadProduct(item!.id);
      onItemChange(updatedItem);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["packaging", item?.id] }),
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["product-audit", item?.id] })
      ]);
    }
  });
  const compatibility = useMutation({
    mutationFn: () => checkCompatibility(
      selectedPackaging, selectedBin, Number(quantity)
    )
  });
  const returnToDraft = useMutation({
    mutationFn: () => changeProductStatus(item!.id, "DRAFT", item!.version),
    onSuccess: async (updatedItem) => {
      onItemChange(updatedItem);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["product-audit", item?.id] })
      ]);
    }
  });
  const bins = structure.data?.filter((node) => node.type === "STORAGE_BIN") ?? [];
  const warehouses = structure.data?.filter((node) => node.type === "WAREHOUSE") ?? [];
  const policyNumbersValid = [policySafety, policyMinimum, policyMaximum,
    policyMultiple, policyLeadTime].every((value) => Number.isFinite(Number(value)))
    && Number(policySafety) >= 0
    && Number(policyMinimum) >= Number(policySafety)
    && Number(policyMaximum) > Number(policyMinimum)
    && Number(policyMultiple) > 0
    && Number(policyLeadTime) >= 0
    && Number.isInteger(Number(policyLeadTime))
    && (policyMinimumOrder === "" || Number(policyMinimumOrder) > 0);

  function openPolicy(policy: MaterialStockPolicy | null) {
    setEditedPolicy(policy);
    setPolicyWarehouseId(policy?.warehouseId ?? "");
    setPolicySafety(String(policy?.safetyStockQuantity ?? 0));
    setPolicyMinimum(String(policy?.minimumQuantity ?? 0));
    setPolicyMaximum(String(policy?.maximumQuantity ?? 1));
    setPolicyMultiple(String(policy?.replenishmentMultiple ?? 1));
    setPolicyMinimumOrder(policy?.minimumReplenishmentQuantity == null
      ? "" : String(policy.minimumReplenishmentQuantity));
    setPolicyLeadTime(String(policy?.leadTimeDays ?? 0));
    setPolicyExecutionMode(policy?.executionMode ?? "DIRECT_SUPPLIER_ORDER");
    setPolicyOutputProfileId(policy?.outputProfileId
      ?? replenishmentOutputProfiles.data?.[0]?.id ?? "");
    setPolicyStatus(policy?.status ?? "ACTIVE");
    setPolicyOpen(true);
  }
  const packagingNumbersValid =
    Number.isFinite(Number(baseQty)) && Number(baseQty) > 0 &&
    Number.isFinite(Number(netWeight)) && Number(netWeight) >= 0 &&
    Number.isFinite(Number(grossWeight)) && Number(grossWeight) > 0;

  useEffect(() => {
    setSelectedPackaging("");
    setPackagingDefinitionId("");
    setEditedName(item?.displayName ?? "");
    setEditedRotationPolicy(item?.rotationPolicy ?? "FIFO");
    setEditedMinimumShelfLife(String(
      item?.minimumRemainingShelfLifeDays ?? 0
    ));
    setEditedLotMixing(item?.lotMixingAllowed ?? true);
    setEditedPartialHu(item?.partialHuAllowed ?? true);
    setRotationReason("");
  }, [item?.id]);

  useEffect(() => {
    const firstActive = packaging.data?.find((pack) => pack.status === "ACTIVE");
    if (!selectedPackaging && firstActive) {
      setSelectedPackaging(firstActive.id);
    }
  }, [packaging.data, selectedPackaging]);

  function submitPackaging() {
    setPackagingValidationVisible(true);
    if (!packagingDefinitionId || !uom.trim() || !packagingNumbersValid ||
        Number(grossWeight) < Number(netWeight)) {
      return;
    }
    addPackaging.mutate();
  }

  return (
    <Dialog open={Boolean(item)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{item?.code} · {item?.displayName}</DialogTitle>
      <DialogContent>
        {item && <Stack spacing={3}>
          <Stack direction="column" spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexGrow: 1 }}>
              <Chip label={`Base ${item.baseUom}`} />
              <Chip label={t(`masterData.common.statuses.${item.status}`, { defaultValue: item.status })} color={item.status === "ACTIVE" ? "success" : "default"} />
              {item.lotTracking && <Chip label={t("masterData.products.lotTracking")} />}
              {item.serialTracking && <Chip label={t("masterData.products.serialTracking")} />}
              {item.expiryTracking && <Chip label={t("masterData.products.expiryValue", { days: item.shelfLifeDays })} />}
              <Chip label={item.abcClass ? t("masterData.products.abcClassValue", { value: item.abcClass }) : t("masterData.products.abcUnclassified")} color={item.abcClass ? "info" : "default"} />
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap"
              justifyContent="flex-end" sx={{ width: "100%",
                "& .MuiButton-root": { flexShrink: 0, maxWidth: "100%" } }}>
              {item.status === "ACTIVE" && (
                <Button variant="outlined" color="warning"
                  startIcon={<ArrowBackOutlined />}
                  disabled={returnToDraft.isPending}
                  onClick={() => returnToDraft.mutate()}>
                  {returnToDraft.isPending ? t("masterData.products.updatePending") : t("masterData.products.returnDraft")}
                </Button>
              )}
              {item.status !== "ARCHIVED" && (
                <>
                  <Button variant="outlined" startIcon={<EditOutlined />}
                    onClick={() => {
                      setEditedName(item.displayName);
                      setEditedAbcClass(item.abcClass ?? "");
                      setEditedSupplierIds(item.supplierIds);
                      setEditedPrimarySupplierId(item.preferredSupplierId ?? "");
                      setEditOpen(true);
                    }}>
                    {t("masterData.common.edit")}
                  </Button>
                  <Button variant="outlined" color="error"
                    startIcon={<ArchiveOutlined />}
                    onClick={() => setArchiveOpen(true)}>
                    {t("masterData.common.archive")}
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
          {returnToDraft.isError && (
            <Alert severity="error">
              {t("masterData.products.statusUpdateError")}
            </Alert>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t("masterData.products.outboundPolicy")}</Typography>
            {item.status !== "ARCHIVED" && canManageRotation && (
              <Button variant="outlined" startIcon={<EditOutlined />}
                onClick={() => setRotationOpen(true)}>
                {t("masterData.common.edit")}
              </Button>
            )}
          </Stack>
          <Card variant="outlined"><CardContent>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography fontWeight={800}>
                  {t("masterData.products.rotation")}
                </Typography>
                <Chip size="small" color="secondary" label={item.rotationPolicy} />
                <Typography color="text.secondary">
                  {t(`masterData.products.rotationDescriptions.${item.rotationPolicy}`)}
                </Typography>
              </Stack>
              {item.expiryTracking && (
                <Typography color="text.secondary">
                  {t("masterData.products.minimumRemainingShelfLifeValue", {
                    days: item.minimumRemainingShelfLifeDays ?? 0
                  })}
                </Typography>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={item.lotMixingAllowed
                  ? t("masterData.products.lotMixing") : t("masterData.products.uniqueLot")} />
                <Chip size="small" label={item.partialHuAllowed
                  ? t("masterData.products.partialPicking") : t("masterData.products.fullHu")} />
              </Stack>
            </Stack>
          </CardContent></Card>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t("masterData.products.stockPolicies")}</Typography>
            {canConfigureReplenishment && item.status !== "ARCHIVED" && (
              <Button variant="outlined" startIcon={<AddOutlined />}
                onClick={() => openPolicy(null)}>
                {t("masterData.products.addStockPolicy")}
              </Button>
            )}
          </Stack>
          {stockPolicies.isError && <Alert severity="error">
            {t("masterData.products.stockPolicyLoadError")}
          </Alert>}
          {stockPolicies.data?.length === 0 && <Alert severity="info">
            {t("masterData.products.noStockPolicy")}
          </Alert>}
          {stockPolicies.data?.map((policy) => (
            <Card key={policy.id} variant="outlined"><CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                alignItems={{ sm: "center" }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={800}>
                    {policy.warehouseCode} · {policy.warehouseDisplayName}
                  </Typography>
                  <Typography color="text.secondary">
                    {t("masterData.products.stockPolicyThresholds", {
                      safety: policy.safetyStockQuantity, minimum: policy.minimumQuantity,
                      maximum: policy.maximumQuantity, unit: policy.baseUom
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("masterData.products.stockPolicyParameters", {
                      multiple: policy.replenishmentMultiple,
                      minimumOrder: policy.minimumReplenishmentQuantity ?? "—",
                      leadTime: policy.leadTimeDays, unit: policy.baseUom
                    })}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip size="small" label={policy.clientCode ?? t("masterData.products.sharedStock")} />
                    <Chip size="small" color={policy.status === "ACTIVE" ? "success" : "default"}
                      label={t(`masterData.common.statuses.${policy.status}`, { defaultValue: policy.status })} />
                    <Chip size="small" variant="outlined"
                      label={t(`masterData.products.executionModes.${policy.executionMode}`)} />
                    {policy.outputProfileDisplayName && <Chip size="small" variant="outlined"
                      label={policy.outputProfileDisplayName} />}
                  </Stack>
                  {(() => {
                    const evaluation = stockPolicyEvaluations.data
                      ?.find((entry) => entry.policy.id === policy.id);
                    if (!evaluation) return null;
                    const statusColor = evaluation.evaluationStatus === "HEALTHY"
                      ? "success" : evaluation.evaluationStatus === "REPLENISH"
                        ? "warning" : evaluation.evaluationStatus === "CRITICAL"
                          ? "error" : "default";
                    return <Box sx={{ mt: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center"
                        flexWrap="wrap" useFlexGap>
                        <Chip size="small" color={statusColor}
                          label={t(`masterData.products.stockEvaluationStatuses.${evaluation.evaluationStatus}`)} />
                        <Typography variant="body2">
                          {t("masterData.products.stockEvaluationQuantities", {
                            onHand: evaluation.onHandQuantity,
                            unavailable: evaluation.unavailableQuantity,
                            reserved: evaluation.reservedQuantity,
                            available: evaluation.availableQuantity,
                            inbound: evaluation.confirmedInboundQuantity,
                            projected: evaluation.projectedAvailableQuantity,
                            unit: policy.baseUom
                          })}
                        </Typography>
                      </Stack>
                      {evaluation.recommendedReplenishmentQuantity > 0 && (
                        <Typography variant="body2" fontWeight={800} color="warning.main" sx={{ mt: 0.75 }}>
                          {t("masterData.products.recommendedReplenishment", {
                            quantity: evaluation.recommendedReplenishmentQuantity,
                            unit: policy.baseUom
                          })}
                        </Typography>
                      )}
                    </Box>;
                  })()}
                </Box>
                {canConfigureReplenishment && item.status !== "ARCHIVED" && (
                  <Button variant="outlined" startIcon={<EditOutlined />}
                    onClick={() => openPolicy(policy)}>{t("masterData.common.edit")}</Button>
                )}
              </Stack>
            </CardContent></Card>
          ))}

          <Typography variant="h6">{t("masterData.products.packaging")}</Typography>
          {!packaging.isLoading && !packaging.isError && packaging.data?.length === 0 && (
            <Alert severity="info">
              {t("masterData.products.noPackaging")}
            </Alert>
          )}
          {packaging.isError && (
            <Alert severity="error">{t("masterData.products.packagingLoadError")}</Alert>
          )}
          {packaging.data?.map((pack) => (
            <Card key={pack.id} variant="outlined"><CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                alignItems={{ sm: "center" }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={800}>
                    {pack.code} · {pack.displayName} · {pack.uom} ×
                    {" "}{pack.baseQuantity}
                  </Typography>
                  <Typography color="text.secondary">
                    <MeasurementValue value={pack.lengthMm} kind="LENGTH" /> ×
                    {" "}<MeasurementValue value={pack.widthMm} kind="LENGTH" /> ×
                    {" "}<MeasurementValue value={pack.heightMm} kind="LENGTH" /> ·
                    {" "}<MeasurementValue value={pack.grossWeightKg} kind="MASS" /> ·
                    {" "}<MeasurementValue value={pack.volumeMm3} kind="VOLUME" />
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip size="small" label={pack.packagingLevel} />
                    {pack.basePackaging &&
                      <Chip size="small" color="primary" label={t("masterData.products.base")} />}
                    {pack.status !== "ACTIVE" &&
                      <Chip size="small" label={t("masterData.products.removed")} />}
                  </Stack>
                </Box>
                {pack.status === "ACTIVE" && item.status !== "ARCHIVED" && (
                  <Button variant="outlined" color="error"
                    startIcon={<DeleteOutlineOutlined />}
                    onClick={() => setPackagingToArchive(pack)}>
                    {t("masterData.products.removed")}
                  </Button>
                )}
              </Stack>
            </CardContent></Card>
          ))}
          {item.status !== "ARCHIVED" && <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <FormControl fullWidth required
              error={packagingValidationVisible && !packagingDefinitionId}>
              <InputLabel>{t("masterData.products.masterPackaging")}</InputLabel>
              <Select value={packagingDefinitionId} label={t("masterData.products.masterPackaging")}
                onChange={(event) => setPackagingDefinitionId(event.target.value)}>
                {packagingDefinitions.data
                  ?.filter((definition) => definition.status === "ACTIVE" &&
                    !packaging.data?.some((pack) =>
                      pack.packagingDefinitionId === definition.id
                      && pack.status === "ACTIVE"))
                  .map((definition) => (
                    <MenuItem key={definition.id} value={definition.id}>
                      {definition.code} · {definition.displayName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField label="UOM" required value={uom}
              error={packagingValidationVisible && !uom.trim()}
              onChange={(e) => setUom(e.target.value.toUpperCase())} />
            <TextField label={t("masterData.products.baseQuantity")} type="number" value={baseQty} onChange={(e) => setBaseQty(e.target.value)} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField label={`${t("masterData.products.netWeight")} (${massUnit})`} type="number" value={netWeight}
              inputProps={{ min: 0, step: massStep }} onChange={(e) =>
                setNetWeight(clampNonNegativeMeasurementInput(e.target.value))} />
            <TextField label={`${t("masterData.products.grossWeight")} (${massUnit})`} type="number" value={grossWeight}
              inputProps={{ min: 0, step: massStep }} onChange={(e) =>
                setGrossWeight(clampNonNegativeMeasurementInput(e.target.value))} />
            <FormControl fullWidth>
              <InputLabel>{t("masterData.products.level")}</InputLabel>
              <Select value={packagingLevel} label={t("masterData.products.level")}
                onChange={(event) => setPackagingLevel(
                  event.target.value as "EACH" | "INNER" | "CASE" | "PALLET"
                )}>
                <MenuItem value="EACH">{t("masterData.products.each")}</MenuItem>
                <MenuItem value="INNER">{t("masterData.products.inner")}</MenuItem>
                <MenuItem value="CASE">{t("masterData.products.case")}</MenuItem>
                <MenuItem value="PALLET">{t("masterData.products.pallet")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <FormControlLabel control={<Checkbox checked={basePackaging}
            onChange={(event) => setBasePackaging(event.target.checked)} />}
            label={t("masterData.products.basePackaging")} />
          {packagingValidationVisible &&
            Number(grossWeight) < Number(netWeight) && (
              <Alert severity="warning">
                {t("masterData.products.grossWeightWarning")}
              </Alert>
            )}
          {packagingValidationVisible && !packagingNumbersValid && (
            <Alert severity="warning">
              {t("masterData.products.positiveValuesWarning")}
            </Alert>
          )}
          {addPackaging.isError && (
            <Alert severity="error">{t("masterData.products.packagingSaveError")}</Alert>
          )}
          <Button variant="outlined" startIcon={<AddOutlined />}
            disabled={addPackaging.isPending} onClick={submitPackaging}>
            {addPackaging.isPending ? t("masterData.products.updatePending") : t("masterData.products.associatePackaging")}
          </Button>
          </>}

          <Typography variant="h6">{t("masterData.products.physicalTest")}</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(180px, 1fr) minmax(180px, 1fr) 120px 150px"
              },
              gap: 1,
              alignItems: "stretch"
            }}
          >
            <FormControl fullWidth><InputLabel>{t("masterData.products.packaging")}</InputLabel>
              <Select value={selectedPackaging} label={t("masterData.products.packaging")}
                disabled={!packaging.data?.length}
                onChange={(e) => setSelectedPackaging(e.target.value)}>
                {packaging.data?.filter((pack) => pack.status === "ACTIVE")
                  .map((pack) => (
                    <MenuItem key={pack.id} value={pack.id}>
                      {pack.code}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth><InputLabel>Bin</InputLabel>
              <Select value={selectedBin} label="Bin" onChange={(e) => setSelectedBin(e.target.value)}>
                {bins.map((bin) => <MenuItem key={bin.id} value={bin.id}>{bin.code}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label={t("masterData.products.quantity")} type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            <Button variant="contained" startIcon={<ScienceOutlined />}
              sx={{ minWidth: 150, minHeight: 56, whiteSpace: "nowrap" }}
              disabled={!selectedPackaging || !selectedBin}
              onClick={() => compatibility.mutate()}>{t("masterData.products.test")}</Button>
          </Box>
          {compatibility.data && (
            <Alert severity={compatibility.data.compatible ? "success" : "error"}>
              {compatibility.data.compatible
                ? t("masterData.products.compatibleOrientation", { orientation: compatibility.data.selectedOrientation })
                : t("masterData.products.rejectedCompatibility", { reasons: compatibility.data.rejectionReasons.join(", ") })}
            </Alert>
          )}

          <Accordion disableGutters elevation={0} variant="outlined">
            <AccordionSummary
              expandIcon={<ExpandMoreOutlined />}
              aria-controls="product-audit-content"
              id="product-audit-header"
            >
              <Typography variant="h6">
                Audit {audit.data ? `(${audit.data.length})` : ""}
              </Typography>
            </AccordionSummary>
            <AccordionDetails id="product-audit-content">
              {audit.isLoading && (
                <Typography color="text.secondary">{t("masterData.products.auditLoading")}</Typography>
              )}
              {audit.isError && (
                <Alert severity="error">{t("masterData.products.auditError")}</Alert>
              )}
              {audit.data?.length === 0 && (
                <Typography color="text.secondary">{t("masterData.products.auditEmpty")}</Typography>
              )}
              <Stack spacing={1.5}>
                {audit.data?.map((entry) => (
                  <Box key={entry.id}>
                    <Typography variant="body2" fontWeight={700}>
                      {entry.entityType} · {entry.action}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(entry.changedAt).toLocaleString("fr-FR")}
                      {" · "}{entry.changedBy}
                      {entry.changedByEmail ? ` · ${entry.changedByEmail}` : ""}
                    </Typography>
                    {entry.reason && (
                      <Typography variant="body2">{entry.reason}</Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t("masterData.common.close")}</Button></DialogActions>
      <Dialog open={policyOpen} onClose={() => setPolicyOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editedPolicy
          ? t("masterData.products.editStockPolicy")
          : t("masterData.products.addStockPolicy")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth required disabled={Boolean(editedPolicy)}>
              <InputLabel>{t("masterData.common.warehouse")}</InputLabel>
              <Select value={policyWarehouseId} label={t("masterData.common.warehouse")}
                onChange={(event) => setPolicyWarehouseId(event.target.value)}>
                {warehouses.filter((warehouse) => warehouse.status === "ACTIVE")
                  .map((warehouse) => <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.displayName}
                  </MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField fullWidth type="number" label={t("masterData.products.safetyStock")}
                value={policySafety} inputProps={{ min: 0 }}
                onChange={(e) => setPolicySafety(clampNonNegativeMeasurementInput(e.target.value))} />
              <TextField fullWidth type="number" label={t("masterData.products.minimumStock")}
                value={policyMinimum} inputProps={{ min: 0 }}
                onChange={(e) => setPolicyMinimum(clampNonNegativeMeasurementInput(e.target.value))} />
              <TextField fullWidth type="number" label={t("masterData.products.maximumStock")}
                value={policyMaximum} inputProps={{ min: 0 }}
                onChange={(e) => setPolicyMaximum(clampNonNegativeMeasurementInput(e.target.value))} />
            </Stack>
            <TextField select fullWidth label={t("masterData.products.executionMode")}
              value={policyExecutionMode}
              onChange={(e) => setPolicyExecutionMode(e.target.value as
                "DIRECT_SUPPLIER_ORDER" | "PURCHASE_REQUEST")}>
              <MenuItem value="DIRECT_SUPPLIER_ORDER">
                {t("masterData.products.executionModes.DIRECT_SUPPLIER_ORDER")}
              </MenuItem>
              <MenuItem value="PURCHASE_REQUEST">
                {t("masterData.products.executionModes.PURCHASE_REQUEST")}
              </MenuItem>
            </TextField>
            <TextField select fullWidth label={t("masterData.products.outputProfile")}
              value={policyOutputProfileId}
              onChange={(e) => setPolicyOutputProfileId(e.target.value)}>
              <MenuItem value="">{t("masterData.products.noOutputProfile")}</MenuItem>
              {(replenishmentOutputProfiles.data ?? []).map((profile) =>
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.displayName}
                </MenuItem>)}
            </TextField>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField fullWidth type="number" label={t("masterData.products.replenishmentMultiple")}
                value={policyMultiple} inputProps={{ min: 0 }}
                onChange={(e) => setPolicyMultiple(clampNonNegativeMeasurementInput(e.target.value))} />
              <TextField fullWidth type="number" label={t("masterData.products.minimumReplenishment")}
                value={policyMinimumOrder} inputProps={{ min: 0 }}
                onChange={(e) => setPolicyMinimumOrder(clampNonNegativeMeasurementInput(e.target.value))} />
              <TextField fullWidth type="number" label={t("masterData.products.leadTimeDays")}
                value={policyLeadTime} inputProps={{ min: 0, step: 1 }}
                onChange={(e) => setPolicyLeadTime(clampNonNegativeMeasurementInput(e.target.value))} />
            </Stack>
            {editedPolicy && <TextField select fullWidth label={t("masterData.common.status")}
              value={policyStatus}
              onChange={(e) => setPolicyStatus(e.target.value as "ACTIVE" | "INACTIVE")}>
              <MenuItem value="ACTIVE">{t("masterData.common.statuses.ACTIVE")}</MenuItem>
              <MenuItem value="INACTIVE">{t("masterData.common.statuses.INACTIVE")}</MenuItem>
            </TextField>}
            <Alert severity="info">{t("masterData.products.stockPolicyRuleHelp")}</Alert>
            {saveStockPolicy.isError && <Alert severity="error">
              {t("masterData.products.stockPolicySaveError")}
            </Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPolicyOpen(false)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained" disabled={!policyWarehouseId || !policyNumbersValid || saveStockPolicy.isPending}
            onClick={() => saveStockPolicy.mutate()}>{t("masterData.common.save")}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth
        maxWidth="sm">
        <DialogTitle>{t("masterData.products.editTitle", { code: item?.code })}</DialogTitle>
        <DialogContent>
          <TextField fullWidth required label={t("masterData.common.name")} value={editedName}
            onChange={(event) => setEditedName(event.target.value)}
            sx={{ mt: 1 }} />
          <TextField select fullWidth label={t("masterData.products.abcClass")} value={editedAbcClass}
            onChange={(event) => setEditedAbcClass(event.target.value as "A" | "B" | "C" | "")}
            sx={{ mt: 2 }}>
            <MenuItem value="">{t("masterData.products.abcUnclassified")}</MenuItem>
            {(["A", "B", "C"] as const).map((value) => <MenuItem key={value} value={value}>{t("masterData.products.abcClassValue", { value })}</MenuItem>)}
          </TextField>
          <FormControl fullWidth required sx={{ mt: 2 }}><InputLabel>{t("masterData.products.suppliers")}</InputLabel>
            <Select multiple value={editedSupplierIds} label={t("masterData.products.suppliers")}
              onChange={(event) => {
                const values = typeof event.target.value === "string"
                  ? event.target.value.split(",") : event.target.value;
                setEditedSupplierIds(values);
                if (!values.includes(editedPrimarySupplierId)) setEditedPrimarySupplierId(values[0] ?? "");
              }}>
              {(suppliers.data ?? []).filter((entry) => entry.status === "ACTIVE").map((entry) =>
                <MenuItem key={entry.id} value={entry.id}>{entry.code} · {entry.displayName}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField select fullWidth required label={t("masterData.products.primarySupplier")}
            value={editedPrimarySupplierId} disabled={!editedSupplierIds.length}
            onChange={(event) => setEditedPrimarySupplierId(event.target.value)} sx={{ mt: 2 }}>
            {(suppliers.data ?? []).filter((entry) => editedSupplierIds.includes(entry.id)).map((entry) =>
              <MenuItem key={entry.id} value={entry.id}>{entry.code} · {entry.displayName}</MenuItem>)}
          </TextField>
          {editItem.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t("masterData.common.operationError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained"
            disabled={!editedName.trim() || !editedSupplierIds.length || !editedPrimarySupplierId || editItem.isPending}
            onClick={() => editItem.mutate()}>
            {t("masterData.common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={archiveOpen} onClose={() => setArchiveOpen(false)}
        fullWidth maxWidth="sm">
        <DialogTitle>{t("masterData.products.archiveTitle", { code: item?.code })}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t("masterData.products.archiveHelp")}
          </Alert>
          {archiveItem.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t("masterData.common.operationError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveOpen(false)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained" color="error"
            disabled={archiveItem.isPending}
            onClick={() => archiveItem.mutate()}>
            {t("masterData.common.archive")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={rotationOpen} onClose={() => setRotationOpen(false)}
        fullWidth maxWidth="sm">
        <DialogTitle>{t("masterData.products.rotationTitle", { code: item?.code })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t("masterData.products.rotation")}</InputLabel>
              <Select value={editedRotationPolicy} label={t("masterData.products.rotation")}
                onChange={(event) => setEditedRotationPolicy(event.target.value as
                  "FIFO" | "FEFO" | "LIFO" | "NONE")}>
                <MenuItem value="FIFO">{t("masterData.products.fifo")}</MenuItem>
                {item?.expiryTracking && <MenuItem value="FEFO">{t("masterData.products.fefoDescription")}</MenuItem>}
                <MenuItem value="LIFO">{t("masterData.products.lifo")}</MenuItem>
                <MenuItem value="NONE">{t("masterData.products.noRotation")}</MenuItem>
              </Select>
            </FormControl>
            {item?.expiryTracking && (
              <TextField type="number"
                label={t("masterData.products.minimumShelfLife")}
                value={editedMinimumShelfLife}
                onChange={(event) => setEditedMinimumShelfLife(event.target.value)} />
            )}
            <FormControlLabel control={<Checkbox checked={editedLotMixing}
              onChange={(event) => setEditedLotMixing(event.target.checked)} />}
              label={t("masterData.products.lotMixing")} />
            <FormControlLabel control={<Checkbox checked={editedPartialHu}
              onChange={(event) => setEditedPartialHu(event.target.checked)} />}
              label={t("masterData.products.partialPicking")} />
            <TextField required multiline minRows={3}
              label={t("masterData.products.changeReason")}
              value={rotationReason}
              onChange={(event) => setRotationReason(event.target.value)}
              helperText={t("masterData.common.requiredMin3")} />
            {editRotation.isError && (
              <Alert severity="error">
                {t("masterData.products.rotationEditError")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotationOpen(false)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained"
            disabled={rotationReason.trim().length < 3 || editRotation.isPending}
            onClick={() => editRotation.mutate()}>
            {t("masterData.common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={packagingToArchive !== null}
        onClose={() => setPackagingToArchive(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("masterData.products.removePackaging")}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t("masterData.products.removePackagingHelp", { code: packagingToArchive?.code })}
          </Alert>
          {archivePackaging.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t("masterData.products.removePackagingError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackagingToArchive(null)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained" color="error"
            disabled={archivePackaging.isPending}
            onClick={() => archivePackaging.mutate()}>
            {t("masterData.products.remove")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
